import addYears from 'date-fns/addYears';
import { useContext, useEffect, useMemo, useState } from 'react';
import IdentityContext from '../../../contexts/IdentityContext';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { Medialink } from '../_api/MedialinksApi';
import { Note, getNotes } from '../_api/NotesApi';
import { SharedSlideSet, SlideSet } from '../_api/SlideSetApi';
import { SharedUserMaterial, UserMaterial } from '../_api/UserMaterialApi';
import { Whitepage } from '../_api/WhitepageApi';
import MaterialFiltersContext from '../_contexts/MaterialFiltersContext';
import ModuleContext from '../_contexts/ModuleContext';
import SharedMaterialContext from '../_contexts/SharedMaterialContext';
import TableOfContentsContext from '../_contexts/TableOfContentsContext';
import {
  MEDIA_CONTENT_TYPE_SHARED_USER_MATERIAL,
  MEDIA_CONTENT_TYPE_SLIDE_SETS,
  MEDIA_CONTENT_TYPE_USER_MATERIAL,
  MEDIA_CONTENT_TYPE_WHITEPAGE,
} from '../browse/components/MediaContentTypesSelect';
import useDigibook from './useDigibook';
import useFavorites from './useFavorites';
import useMedialinks from './useMedialinks';
import useSlideSets from './useSlideSets';
import useUserMaterial from './useUserMaterial';
import useWhitepages from './useWhitepages';

export const ID_NOTES_LINK = 'notesLinkId';

const filterMedialinksByMediaContentType = (mediaContentTypeId: string) => (medialink: Medialink) => {
  return medialink.contentType.id === mediaContentTypeId || medialink.contentType.category === mediaContentTypeId;
};

export type FilteredMaterials = {
  userMaterial: UserMaterial[];
  sharedMaterial: (SharedUserMaterial | SharedSlideSet)[];
  whitepages: Whitepage[];
  medialinks: Medialink[];
  slideSets: SlideSet[];
};

export default function useFilteredMaterials() {
  const [materials, setMaterials] = useState<FilteredMaterials>({
    userMaterial: [],
    sharedMaterial: [],
    whitepages: [],
    medialinks: [],
    slideSets: [],
  });

  const { selectedModule } = useContext(ModuleContext);
  const { filterOnFavorites, mediaContentTypeId } = useContext(MaterialFiltersContext);
  const { userMaterial } = useUserMaterial();
  const { sharedMaterial } = useContext(SharedMaterialContext);
  const { medialinks } = useMedialinks();
  const { whitepages } = useWhitepages();
  const { slideSets } = useSlideSets();
  const { getFavoritesForModule } = useFavorites();
  const { digibook, buildCodexUrl } = useDigibook();
  const handleError = useThrowAsyncError();
  const { selectedNode } = useContext(TableOfContentsContext);
  const { identity } = useContext(IdentityContext);

  const favorites = getFavoritesForModule(selectedModule?.id);

  const [notes, setNotes] = useState<Note[]>([]);

  useEffect(() => {
    async function fetchNotes() {
      if (!selectedModule || !selectedNode) return;

      try {
        const { data } = await getNotes(selectedModule.id, selectedNode.id);

        setNotes(data);
      } catch (e) {
        handleError(e);
      }
    }

    if (identity.isAuthenticated) fetchNotes();
  }, [handleError, identity.isAuthenticated, selectedModule, selectedNode]);

  const notesLink = useMemo(() => {
    if (!selectedModule || !digibook || notes.length === 0) return;

    const pageRange = digibook.pageRanges.find(pageRange => pageRange.nodeId === selectedNode?.id);

    const hasMappedNode = selectedNode?.id === 'general' || pageRange;
    const pageNumber = (pageRange || {}).from;

    if (!hasMappedNode) return;

    return buildCodexUrl({
      digibookId: digibook.id,
      moduleId: selectedModule.id,
      page: pageNumber,
      drawerMode: 'notes',
    });
  }, [buildCodexUrl, digibook, notes, selectedModule, selectedNode]);

  useEffect(() => {
    const filteredMaterials = { whitepages, sharedMaterial, userMaterial, medialinks, slideSets };

    if (notesLink && filteredMaterials.userMaterial.find(x => x.id === ID_NOTES_LINK) === undefined) {
      // the value for lastUpdatedAt is added to make sure the notesLink is always displayed on top of the list
      filteredMaterials.userMaterial.unshift({
        id: ID_NOTES_LINK,
        name: '',
        moduleId: '',
        node: '',
        lastUpdatedAt: addYears(new Date(), 1).toString(),
        maxScore: 0,
        href: notesLink,
      });
    }

    if (filterOnFavorites) {
      filteredMaterials.whitepages = whitepages.filter(x => favorites.whitepages?.includes(x.id));
      filteredMaterials.sharedMaterial = sharedMaterial.filter(
        x => x.materialType === 'user-material' && favorites.userMaterial?.includes(x.id), // favorite slide sets not supported
      );
      filteredMaterials.userMaterial = userMaterial.filter(x => favorites.userMaterial?.includes(x.id));
      filteredMaterials.medialinks = medialinks.filter(x => favorites.medialinks?.includes(x.id));
      filteredMaterials.slideSets = []; // Favourites is not supported
    }

    if (mediaContentTypeId) {
      switch (mediaContentTypeId) {
        case MEDIA_CONTENT_TYPE_WHITEPAGE:
          filteredMaterials.userMaterial = [];
          filteredMaterials.sharedMaterial = [];
          filteredMaterials.slideSets = [];
          filteredMaterials.medialinks = [];
          break;

        case MEDIA_CONTENT_TYPE_SHARED_USER_MATERIAL:
          filteredMaterials.userMaterial = [];
          filteredMaterials.slideSets = [];
          filteredMaterials.whitepages = [];
          filteredMaterials.medialinks = [];
          break;

        case MEDIA_CONTENT_TYPE_USER_MATERIAL:
          filteredMaterials.sharedMaterial = [];
          filteredMaterials.slideSets = [];
          filteredMaterials.whitepages = [];
          filteredMaterials.medialinks = [];
          break;

        case MEDIA_CONTENT_TYPE_SLIDE_SETS:
          filteredMaterials.userMaterial = [];
          filteredMaterials.sharedMaterial = [];
          filteredMaterials.whitepages = [];
          filteredMaterials.medialinks = [];
          break;

        default:
          filteredMaterials.userMaterial = [];
          filteredMaterials.sharedMaterial = [];
          filteredMaterials.whitepages = [];
          filteredMaterials.slideSets = [];
          filteredMaterials.medialinks = filteredMaterials.medialinks.filter(
            filterMedialinksByMediaContentType(mediaContentTypeId),
          );

          break;
      }
    }

    setMaterials(filteredMaterials);
  }, [
    favorites,
    filterOnFavorites,
    mediaContentTypeId,
    whitepages,
    slideSets,
    sharedMaterial,
    userMaterial,
    medialinks,
    notesLink,
  ]);

  return materials;
}
